import { useCallback, useEffect, useState } from "react";
import { Compass, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const PAGES = [
  { name: "news", label: "News" },
  { name: "messages", label: "iMessages" },
  { name: "calendar", label: "Calendar" },
  { name: "tools", label: "Smart Tools" },
];

// How many newcomers sit through each 30-second walkthrough, and which stop loses them.
export const GuideStats = () => {
  const [which, setWhich] = useState("news");
  const [stats, setStats] = useState(null);
  const [failed, setFailed] = useState("");

  const load = useCallback(() => {
    setStats(null);
    setFailed("");
    api.get(`/guide/${which}/stats`)
      .then(({ data }) => setStats(data))
      .catch((e) => {
        console.error("could not load the walkthrough stats", e);
        setFailed("Those numbers would not load. Try again in a moment.");
      });
  }, [which]);

  useEffect(load, [load]);

  const worst = stats?.stops?.length
    ? stats.stops.slice(0, -1).reduce((a, b) => (b.left > a.left ? b : a), stats.stops[0])
    : null;
  const through = stats?.watched
    ? Math.round((stats.finished / stats.watched) * 100) : 0;

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="guide-stats">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
          <Compass className="h-4 w-4 text-brand" /> Page walkthroughs
        </p>
        <div className="ms-auto flex flex-wrap gap-1">
          {PAGES.map((one) => (
            <button
              key={one.name}
              data-testid={`guide-stats-tab-${one.name}`}
              onClick={() => setWhich(one.name)}
              className={`min-h-[28px] rounded-full border px-3 text-[11px] transition-colors ${
                which === one.name ? "border-brand bg-brand/15 text-brand"
                  : "border-white/15 text-ink-soft hover:text-ink"}`}
            >
              {one.label}
            </button>
          ))}
        </div>
      </div>

      {failed ? (
        <p className="mt-4 text-sm text-ink-soft" data-testid="guide-stats-failed">{failed}</p>
      ) : !stats ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-ink-soft" />
      ) : (
        <>
          <div className="mt-4 flex flex-wrap gap-6">
            <p className="text-sm" data-testid="guide-stats-watched">
              <span className="block font-display text-3xl font-bold text-brand">{stats.watched}</span>
              <span className="text-ink-soft">newcomers watched</span>
            </p>
            <p className="text-sm" data-testid="guide-stats-finished">
              <span className="block font-display text-3xl font-bold">{stats.finished}</span>
              <span className="text-ink-soft">stayed to the end</span>
              <span className="block text-[11px] text-ink-soft">{through}% of them</span>
            </p>
            <p className="text-sm" data-testid="guide-stats-skipped">
              <span className="block font-display text-3xl font-bold">{stats.skipped}</span>
              <span className="text-ink-soft">skipped out</span>
            </p>
          </div>

          {stats.watched === 0 ? (
            <p className="mt-4 text-[11px] text-ink-soft" data-testid="guide-stats-empty">
              Nobody has been round this page yet.
            </p>
          ) : (
            <>
              {worst?.left ? (
                <p className="mt-4 rounded-2xl border border-[#F5C451]/30 bg-[#F5C451]/10 p-3 text-sm"
                   data-testid="guide-stats-worst">
                  Most people give up on <b>“{worst.title}”</b> — {worst.left} stopped there.
                </p>
              ) : null}
              <ul className="mt-4 space-y-1.5" data-testid="guide-stats-stops">
                {stats.stops.map((stop, i) => (
                  <li key={stop.key} className="flex items-center gap-2 text-[11px]"
                      data-testid={`guide-stop-${stop.key}`}>
                    <span className="w-5 shrink-0 tabular-nums text-ink-soft">{i + 1}</span>
                    <span className="w-52 shrink-0 truncate text-ink" title={stop.title}>
                      {stop.title}
                    </span>
                    <span className="h-1.5 min-w-[3px] rounded-full bg-brand/70"
                          style={{ width: `${Math.max(3, (stop.reached / Math.max(1, stats.watched)) * 100)}%` }} />
                    <span className="shrink-0 tabular-nums text-ink-soft">
                      {stop.reached} got here{stop.left ? ` · ${stop.left} stopped` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
};
